/**
 * GlobalStoreProvider — React wrapper around GlobalStore.
 *
 * Responsibilities:
 *  1. Calls GET /api/session/bootstrap once after login → populates GlobalStore
 *  2. Provides reactive React hooks (useGlobal, useGlobalStore)
 *  3. On logout: calls GlobalStore.clear()
 *
 * Usage:
 *   // In App.js (inside LicenseProvider / ERPContextProvider):
 *   <GlobalStoreProvider globalContextData={globalContextData}>
 *     {children}
 *   </GlobalStoreProvider>
 *
 *   // In any component:
 *   const gstin    = useGlobal(GK.COMPANY_GSTIN);
 *   const { getAll, set } = useGlobalStore();
 *   const snap     = useGlobalSnapshot(); // entire store as object
 */
import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef
} from 'react';
import GlobalStore, { STORE_CHANGE_EVENT, STORE_READY_EVENT } from './GlobalStore';
import { GK } from './GlobalKeys';
import { API_BASE, getToken } from '../lib/api';

// ─── Context ─────────────────────────────────────────────────────────────────
const GlobalStoreCtx = createContext({
  ready: false,
  bootstrapError: null,
  rebootstrap: () => {},
});

export function useGlobalStoreCtx() { return useContext(GlobalStoreCtx); }

// ─── Provider ────────────────────────────────────────────────────────────────
export function GlobalStoreProvider({ children, globalContextData }) {
  const [ready,          setReady]          = useState(() => GlobalStore.get(GK.BOOTSTRAP_DONE) === true);
  const [bootstrapError, setBootstrapError] = useState(null);
  const didBootstrap = useRef(false);

  const bootstrap = useCallback(async (ctxData) => {
    if (!ctxData) return;
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/session/bootstrap`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      if (!data.success) throw new Error(data.message || 'Bootstrap failed');

      // ── Populate GlobalStore in one shot ──────────────────────────────
      const now = new Date();

      // User
      GlobalStore.setMany({
        [GK.USER_ID]:           ctxData.userId     || ctxData.user_id    || data.user?.UserId    || '',
        [GK.USER_NAME]:         ctxData.username   || ctxData.UserName   || data.user?.UserName  || '',
        [GK.USER_DISPLAY_NAME]: ctxData.username   || data.user?.UserName || '',
        [GK.USER_ROLE]:         ctxData.role       || data.user?.UserGroup || '',
        [GK.USER_GROUP_ID]:     ctxData.groupId    || data.user?.UserGroup || '',
        [GK.USER_LEVEL]:        data.user?.UserLevel || '',
        [GK.USER_TYPE]:         data.user?.UserType  || 'M',
        [GK.USER_EMAIL]:        data.user?.MailID    || ctxData.email  || '',
        [GK.USER_PHONE]:        data.user?.PhoneNo   || '',
        [GK.USER_IMAGE]:        data.user?.ImagePath || '',
        [GK.USER_BRANCH_ID]:    ctxData.branchId   || data.user?.BranchId   || '',
        [GK.USER_BRANCH_NAME]:  ctxData.branchName || data.user?.BranchName || '',

        // Company — ALL from DB (S_Company), no hardcoding
        [GK.COMPANY_ID]:        data.company?.CompId       || ctxData.companyId || '',
        [GK.COMPANY_NAME]:      data.company?.CompName     || ctxData.company?.compName || '',
        [GK.COMPANY_SHORT]:     data.company?.CompInit     || data.company?.SCompName || '',
        [GK.COMPANY_ADDRESS]:   data.company?.CompAddress  || '',
        [GK.COMPANY_CITY]:      data.company?.CompCity     || '',
        [GK.COMPANY_STATE]:     data.company?.CompState    || '',
        [GK.COMPANY_ZIP]:       data.company?.Zip          || '',
        [GK.COMPANY_COUNTRY]:   data.company?.Country      || '',   // raw countryId e.g. "IND"
        [GK.COMPANY_PHONE]:     data.company?.Phone1       || '',
        [GK.COMPANY_EMAIL]:     data.company?.E_Mail       || '',
        [GK.COMPANY_URL]:       data.company?.Url          || '',
        [GK.COMPANY_PAN]:       data.company?.PANNo        || '',
        [GK.COMPANY_GSTIN]:     data.company?.GST          || '',
        [GK.COMPANY_CST]:       data.company?.CST          || '',
        [GK.COMPANY_CURRENCY]:  data.company?.CompCurrency || '',  // raw DB ID e.g. "76"
        [GK.COMPANY_REG]:       data.company?.CompReg      || '',

        // Fiscal year — from S_Company StartMonth/EndMonth (HBS integers) + derived label
        [GK.FISCAL_YEAR]:       data.fiscalYear?.label      || '',
        [GK.FY_START_DATE]:     data.fiscalYear?.startDate  || '',
        [GK.FY_END_DATE]:       data.fiscalYear?.endDate    || '',
        [GK.FY_START_MONTH]:    data.fiscalYear?.startMonth || null,
        [GK.FY_END_MONTH]:      data.fiscalYear?.endMonth   || null,
        // HBS integers from raw DB (for query usage)
        [GK.INT_FY_START]:      data.intFyStart || null,   // raw StartMonth integer
        [GK.INT_FY_END]:        data.intFyEnd   || null,   // raw EndMonth integer

        // Active branch — from BranchMaster
        [GK.BRANCH_ID]:         data.branch?.ID          || ctxData.branchId   || '',
        [GK.BRANCH_NAME]:       data.branch?.Name        || ctxData.branchName || '',
        [GK.BRANCH_LOCATION]:   data.branch?.Location    || '',
        [GK.BRANCH_GSTIN]:      data.branch?.GSTIN       || '',
        [GK.BRANCH_TYPE]:       data.branch?.Branchtype  || '1',
        [GK.BRANCH_WAREHOUSE]:  data.branch?.Defwhouse   || '',
        [GK.BRANCHES_LIST]:     data.branches            || [],

        // Business date — from dbo.englishHBS(GETDATE()) query, never hardcoded
        [GK.BUSINESS_DATE]:     data.businessDate        || now.toISOString().split('T')[0],
        [GK.BUSINESS_DATE_FMT]: formatDisplayDate(data.businessDate || now.toISOString().split('T')[0]),
        [GK.INT_BUSINESS_DATE]: data.intBusinessDate     || null,   // HBS integer
        [GK.BIZ_DAY_STATUS]:    data.bizDayStatus        || 'open',

        // Globalization — ALL from DB (CurrencyMaster + S_Company.Country), never hardcoded
        [GK.CURRENCY]:          data.glob?.currency       || '',    // raw DB currency ID
        [GK.CURRENCY_CODE]:     data.glob?.currencyCode   || '',    // ISO code from CurrencyMaster
        [GK.CURRENCY_NAME]:     data.glob?.currencyName   || '',    // from CurrencyMaster
        [GK.CURRENCY_SYMBOL]:   data.glob?.currencySymbol || '',    // from CurrencyMaster
        [GK.CURRENCY_DECIMALS]: data.glob?.decimals       || 2,
        [GK.GRID_DECIMAL]:      data.glob?.gridDecimal    || '##,##,##0.00',
        [GK.QTY_DECIMAL]:       data.glob?.qtyDecimal     || '##,##,##0.000',
        [GK.DATE_FORMAT]:       data.glob?.dateFormat     || 'DD-MMM-YYYY',  // from system config
        [GK.DATE_SEPARATOR]:    data.glob?.dateSeparator  || '-',
        [GK.DECIMAL_FORMAT]:    data.glob?.decimalFormat  || '##,##,##0.00',
        [GK.LOCALE]:            data.glob?.locale         || '',    // from Country, never hardcoded
        [GK.LANGUAGE]:          data.glob?.language       || '',
        [GK.COUNTRY]:           data.glob?.country        || '',    // resolved country name
        [GK.COUNTRY_ID]:        data.glob?.countryId      || '',    // raw countryId from DB
        [GK.STATE_CODE]:        data.company?.CompState   || '',    // from S_Company
        [GK.TIMEZONE]:          data.glob?.timezone       || '',    // from Country map, never hardcoded

        // Session meta
        [GK.SESSION_ID]:        data.sessionId            || crypto.randomUUID(),
        [GK.LOGIN_TIME]:        now.toISOString(),
        [GK.APP_VERSION]:       data.version              || '',
        [GK.BOOTSTRAP_DONE]:    true,

        // Tenant context — tracked for audit and multi-tenant routing
        [GK.TENANT_CLIENT_ID]:  ctxData.clientId    || ctxData.client_id   || data.company?.CompId || '',
        [GK.TENANT_COMPANY_ID]: ctxData.companyId   || ctxData.compId      || data.company?.CompId || '',
        [GK.TENANT_LOGIN_TIME]: now.toISOString(),
        [GK.TENANT_DB_NAME]:    ctxData.sqlDBName   || ctxData.sqlDb       || '',
      });

      setReady(true);
      setBootstrapError(null);
      try { window.dispatchEvent(new CustomEvent(STORE_READY_EVENT)); } catch (_storeEvt) { /* best-effort */ }
    } catch (err) {
      console.warn('[GlobalStore] Bootstrap failed:', err.message);
      setBootstrapError(err.message);
      // Seed minimum values from existing context so app is not blank
      if (ctxData) {
        GlobalStore.setMany({
          [GK.USER_ID]:           ctxData.userId   || '',
          [GK.USER_NAME]:         ctxData.username || '',
          [GK.USER_ROLE]:         ctxData.role     || '',
          [GK.COMPANY_ID]:        ctxData.companyId || ctxData.clientId || '',
          [GK.COMPANY_NAME]:      ctxData.company?.compName || '',
          [GK.BRANCH_ID]:         ctxData.branchId || '',
          [GK.BRANCH_NAME]:       ctxData.branchName || '',
          [GK.BUSINESS_DATE]:     new Date().toISOString().split('T')[0],
          [GK.INT_BUSINESS_DATE]: null,
          [GK.BOOTSTRAP_DONE]:    false,
        });
      }
      setReady(true); // allow app to continue even if bootstrap fails
    }
  }, []);

  useEffect(() => {
    if (globalContextData && !didBootstrap.current && !GlobalStore.get(GK.BOOTSTRAP_DONE)) {
      didBootstrap.current = true;
      bootstrap(globalContextData);
    }
  }, [globalContextData, bootstrap]);

  const rebootstrap = useCallback(() => {
    didBootstrap.current = false;
    GlobalStore.remove(GK.BOOTSTRAP_DONE);
    setReady(false);
    bootstrap(globalContextData);
  }, [globalContextData, bootstrap]);

  return (
    <GlobalStoreCtx.Provider value={{ ready, bootstrapError, rebootstrap }}>
      {children}
    </GlobalStoreCtx.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * useGlobal(key, fallback?) — reactive access to a single GlobalStore value.
 * Component re-renders only when THAT key changes.
 */
export function useGlobal(key, fallback = null) {
  const [value, setValue] = useState(() => GlobalStore.get(key, fallback));

  useEffect(() => {
    // Keep in sync with external changes — wrap in a function to satisfy set-state-in-effect rule
    const sync = () => { setValue(GlobalStore.get(key, fallback)); };
    sync();

    const handler = (e) => {
      const { key: changedKey, value: newVal } = e.detail || {};
      if (changedKey === key || changedKey === '*') {
        setValue(changedKey === '*' ? fallback : newVal ?? fallback);
      }
    };
    window.addEventListener(STORE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(STORE_CHANGE_EVENT, handler);
  }, [key, fallback]);

  return value;
}

/**
 * useGlobalStore() — returns { get, set, setMany, remove, clear, ready }.
 * Use this when you need imperative access + to know if bootstrap is done.
 */
export function useGlobalStore() {
  const { ready, bootstrapError, rebootstrap } = useGlobalStoreCtx();
  return { ...GlobalStore, ready, bootstrapError, rebootstrap };
}

/**
 * useGlobalSnapshot() — returns reactive snapshot of ALL store values.
 * Use sparingly (re-renders on any change).
 */
export function useGlobalSnapshot() {
  const [snap, setSnap] = useState(() => GlobalStore.snapshot());
  useEffect(() => {
    const handler = () => setSnap(GlobalStore.snapshot());
    window.addEventListener(STORE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(STORE_CHANGE_EVENT, handler);
  }, []);
  return snap;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatDisplayDate(isoDate) {
  if (!isoDate) return '';
  try {
    const [y, m, d] = isoDate.split('-');
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d}-${MONTHS[parseInt(m,10)-1]}-${y}`;
  } catch { return isoDate; }
}

export default GlobalStoreProvider;
